import React, { useState } from "react";
import { attachVideosToExplanation } from "../../../../Services/SystemExplanationsApi";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import LinkIcon from "@mui/icons-material/Link";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import YouTubeIcon from "@mui/icons-material/YouTube";

const AddExplanationVideos = ({ explanationId, onSuccess }) => {
  const [videos, setVideos] = useState([{ title: "", description: "", url: "" }]);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (index, e) => {
    const { name, value } = e.target;
    const newVideos = [...videos];
    newVideos[index][name] = value;
    setVideos(newVideos);
  };

  const handleAddRow = () => {
    setVideos([...videos, { title: "", description: "", url: "" }]);
  };

  const handleRemoveRow = (index) => {
    if (videos.length > 1) {
      const newVideos = videos.filter((_, i) => i !== index);
      setVideos(newVideos);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();

      videos.forEach((vid, index) => {
        formData.append(`videos[${index}][title]`, vid.title);
        formData.append(`videos[${index}][description]`, vid.description);
        formData.append(`videos[${index}][url]`, vid.url);
      });

      await attachVideosToExplanation(explanationId, formData);
      alert("✅ تم رفع الفيديوهات بنجاح!");
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("❌ Error attaching videos:", error);
      alert("فشل رفع الفيديوهات.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar sx={{ bgcolor: '#ff9800', width: 60, height: 60, margin: '0 auto', mb: 1 }}>
          <VideoLibraryIcon sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#333' }}>
          إضافة فيديوهات للشرح
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          أضف روابط فيديوهات YouTube التوضيحية
        </Typography>
        <Chip 
          label={`${videos.length} فيديو`}
          color="warning"
          size="small"
          sx={{ mt: 1 }}
        />
      </Box>

      <Grid container spacing={3}>
        {videos.map((vid, index) => (
          <Grid item xs={12} key={index}>
            <Card 
              elevation={3}
              sx={{
                borderRadius: 2,
                border: '2px solid #ff9800',
                position: 'relative',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: 6,
                }
              }}
            >
              {/* رقم الفيديو */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: 20,
                  bgcolor: '#ff9800',
                  color: 'white',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '1.2rem',
                  boxShadow: 3,
                  zIndex: 1
                }}
              >
                {index + 1}
              </Box>

              {/* زر الحذف */}
              {videos.length > 1 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: -15,
                    right: 20,
                    zIndex: 1
                  }}
                >
                  <Tooltip title="حذف الفيديو">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleRemoveRow(index)}
                      sx={{
                        bgcolor: '#ffebee',
                        '&:hover': {
                          bgcolor: '#ffcdd2',
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              <CardContent sx={{ pt: 4 }}>
                <Grid container spacing={2}>
                  {/* عنوان الفيديو */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <TitleIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            عنوان الفيديو
                          </Typography>
                        </Box>
                        <TextField
                          name="title"
                          value={vid.title}
                          onChange={(e) => handleChange(index, e)}
                          fullWidth
                          required
                          placeholder="أدخل عنوان الفيديو..."
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* وصف الفيديو */}
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ bgcolor: '#fff3e0', height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <DescriptionIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                            وصف الفيديو
                          </Typography>
                        </Box>
                        <TextField
                          name="description"
                          value={vid.description}
                          onChange={(e) => handleChange(index, e)}
                          fullWidth
                          required
                          placeholder="أدخل وصف الفيديو..."
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* رابط الفيديو */}
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ bgcolor: '#ffebee' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <YouTubeIcon sx={{ color: '#f44336', fontSize: 22 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                            رابط الفيديو على YouTube
                          </Typography>
                          <Chip label="مطلوب" size="small" color="error" />
                        </Box>
                        <TextField
                          name="url"
                          value={vid.url}
                          onChange={(e) => handleChange(index, e)}
                          fullWidth
                          required
                          placeholder="https://www.youtube.com/watch?v=..."
                          variant="outlined"
                          size="small"
                          sx={{ bgcolor: 'white' }}
                          InputProps={{
                            startAdornment: <LinkIcon sx={{ mr: 1, color: '#f44336' }} />
                          }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                          الصق رابط الفيديو من YouTube
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* أزرار الإضافة والإرسال */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddRow}
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              borderWidth: 2,
              borderStyle: 'dashed',
              borderColor: '#ff9800',
              color: '#ff9800',
              fontWeight: 'bold',
              '&:hover': {
                borderWidth: 2,
                borderColor: '#f57c00',
                bgcolor: '#fff3e0'
              }
            }}
          >
            إضافة فيديو آخر
          </Button>
        </Grid>
        <Grid item xs={12} md={6}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
            sx={{
              py: 1.5,
              fontWeight: 'bold',
              fontSize: '1.1rem',
              background: 'linear-gradient(45deg, #ff9800 30%, #ffa726 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #f57c00 30%, #ff9800 90%)',
                transform: 'scale(1.02)',
                transition: 'all 0.2s'
              },
              '&:disabled': {
                background: '#e0e0e0'
              }
            }}
          >
            {submitting ? 'جاري الرفع...' : 'رفع الفيديوهات'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddExplanationVideos;
